import * as THREE from 'three';
import { taperedTube } from './human-armors.js';
import { forgedBlade, tileWeaponPlate, weaponBox } from './weapon-geometry.js';

/**
 * The weapons a human rig can carry, drawn as plain working gear: forged iron and steel gone dull, ash and oak, leather wraps and cord,
 * horn and bone for small fittings. Nothing is gilded or jewelled (`docs/art-direction.md`: gear is worn and practical, weapons sized for a
 * real grip). Every wielded weapon is drawn down -Y from the grip's centre with its head toward -Y; a blade's edges lie along ±X and its
 * flats face ±Z, so the hand's `GRIP_ROLL` sets the edge leading in a cut. An axe's bit stands out toward +X, which is the side that leads.
 */
export interface WeaponKit {
  mesh: (parent: THREE.Object3D, geometry: THREE.BufferGeometry, material: THREE.Material, x?: number, y?: number, z?: number) => THREE.Mesh;
  tube: (parent: THREE.Object3D, material: THREE.Material, top: number, bottom: number, length: number, x?: number, y?: number, z?: number, sides?: number) => THREE.Mesh;
  box: (parent: THREE.Object3D, material: THREE.Material, w: number, h: number, d: number, x?: number, y?: number, z?: number) => THREE.Mesh;
  ellipsoid: (parent: THREE.Object3D, material: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number) => THREE.Mesh;
  /** A bandit's gear is drawn cruder still: bare bars for guards, no fuller, a bound grip. */
  rough: boolean;
  /** Dull steel for edges, darker forged iron for heads, guards and fittings; ash for hafts, leather and dark cord for grips, horn for small fittings. */
  material: { blade: THREE.Material; iron: THREE.Material; wood: THREE.Material; leather: THREE.Material; cord: THREE.Material; bone: THREE.Material; feather: THREE.Material };
}

type V2 = [x: number, y: number];
const outline = (points: V2[]) => { const shape = new THREE.Shape(); points.forEach(([x, y], i) => i ? shape.lineTo(x, y) : shape.moveTo(x, y)); shape.closePath(); return shape; };
/** A flat plate cut to `points`, `thick` through (±Z), its edges softened. */
const plate = (k: WeaponKit, group: THREE.Object3D, material: THREE.Material, points: V2[], thick: number, soften = .003, x = 0, y = 0, z = 0) => {
  const geometry = tileWeaponPlate(new THREE.ExtrudeGeometry(outline(points), { depth: thick, bevelEnabled: soften > 0, bevelSize: soften, bevelThickness: soften * .7, bevelSegments: 1, steps: 1 }));
  if (material === k.material.wood) {
    const uv = geometry.getAttribute('uv'); for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getY(i), uv.getX(i));
  }
  return k.mesh(group, geometry, material, x, y, z - thick / 2);
};
/** A solid of revolution; the profile may run either way up the axis, the faces always look out. */
const lathe = (k: WeaponKit, group: THREE.Object3D, material: THREE.Material, profile: V2[], sides = 8, x = 0, y = 0, z = 0) => {
  const up = profile.length > 1 && profile[0][1] > profile[profile.length - 1][1] ? [...profile].reverse() : profile;
  return k.mesh(group, new THREE.LatheGeometry(up.map(([r, h]) => new THREE.Vector2(r, h)), sides), material, x, y, z);
};
/** `n` points of an arc about (cx, cy), from angle `a` to `b`. */
const arc = (cx: number, cy: number, radius: number, a: number, b: number, n: number): V2[] =>
  Array.from({ length: n + 1 }, (_, i) => { const t = a + (b - a) * i / n; return [cx + Math.cos(t) * radius, cy + Math.sin(t) * radius] as V2; });

/** The held blades' sizes (m), drawn down -Y from the grip: the grip's length (the pommel sits at 0.6 of it above the hand), the crossguard's width, where the blade starts below the hand, its length and half width, and the point beyond it. */
export type BladeSpec = { grip: number; guard: number; from: number; length: number; width: number; tip: number };

const GRIP_R = .0165;
/** A grip wrapped in leather, bound with cord at its ends and between turns. */
function wrapped(k: WeaponKit, group: THREE.Object3D, length: number, y = 0, radius = GRIP_R) {
  k.tube(group, k.material.leather, radius, radius, length, 0, y, 0, 12);
  if (k.rough) { for (const at of [-.5, .5]) k.tube(group, k.material.cord, radius + .001, radius + .001, .008, 0, y + length * at, 0, 10); return; }
  const turns = Math.max(3, Math.round(length / .018));
  const points = Array.from({ length: turns * 12 + 1 }, (_, i) => {
    const t = i / (turns * 12), a = t * turns * Math.PI * 2;
    return new THREE.Vector3(Math.cos(a) * radius, y + length * (t - .5), Math.sin(a) * radius);
  });
  k.mesh(group, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), turns * 12, .0008, 4), k.material.cord);
  for (const at of [-.5, .5]) ring(k, group, y + length * at, radius * .9, .007);
}
/** A ferrule or socket of forged iron. */
const ring = (k: WeaponKit, group: THREE.Object3D, y: number, radius: number, length: number) => k.tube(group, k.material.iron, radius + .0025, radius + .004, length, 0, y, 0, 10);
/** A haft of ash from `butt` (+Y) to `head` (-Y). */
function haft(k: WeaponKit, group: THREE.Object3D, butt: number, head: number, radius = GRIP_R, sides = 8) {
  const length = butt - head;
  return k.tube(group, k.material.wood, radius * .94, radius * 1.04, length, 0, (butt + head) / 2, 0, Math.max(12, sides));
}
/** A flat leaf head with a ridge down the middle, from `from` (the socket) down to the point `-from - length`. */
function leaf(k: WeaponKit, group: THREE.Object3D, from: number, length: number, half: number) {
  forgedBlade(k, group, [[-from, half * .38, .005], [-(from + length * .28), half, .005], [-(from + length * .64), half * .78, .004], [-(from + length * .88), half * .36, .0025], [-(from + length), .0002, .0003]], false);
  lathe(k, group, k.material.iron, [[GRIP_R * .95, -from + .035], [GRIP_R, -from], [half * .38, -from - .025]], 12);
}

export function javelinHead(k: WeaponKit, group: THREE.Object3D, length = .15) { leaf(k, group, 0, length, .023); }

/** A straight blade's outline: the guard's bar, the blade tapering to its point. */
function straightBlade(k: WeaponKit, group: THREE.Object3D, spec: BladeSpec) {
  const { from, length, width: w } = spec, end = from + length;
  forgedBlade(k, group, [[-from, w * .86, .0048], [-from - length * .08, w * .86, .0048],
    [-from - length * .66, w * .68, .0038], [-end, w * .44, .0025], [-end - spec.tip, .00015, .00025]], !k.rough && length > .3);
}

/** Swept steel quillons, a peened wheel pommel and a narrow tang collar. */
export function hilt(k: WeaponKit, group: THREE.Object3D, spec: Pick<BladeSpec, 'grip' | 'guard' | 'from'>) {
  wrapped(k, group, spec.grip);
  const half = spec.guard / 2, y = -spec.from + .004;
  plate(k, group, k.material.iron, [[-half, y - .015], [-half * .92, y - .023], [-half * .32, y - .007], [0, y - .009],
    [half * .32, y - .007], [half * .92, y - .023], [half, y - .015], [half * .94, y - .001], [half * .3, y + .012],
    [-half * .3, y + .012], [-half * .94, y - .001]], .022, .0025);
  const pommel = k.mesh(group, new THREE.CylinderGeometry(spec.grip > .2 ? .029 : .023, spec.grip > .2 ? .029 : .023, .018, 12), k.material.iron, 0, spec.grip * .6, 0);
  pommel.rotation.x = Math.PI / 2;
  for (const side of [-1, 1]) k.ellipsoid(group, k.material.blade, 0, spec.grip * .6, side * .010, .014, .014, .003);
  ring(k, group, y + .016, GRIP_R * .93, .018);
}

/** A sword or dagger: a wrapped grip, a plain wheel pommel, a bar guard and a tapered blade. */
export function sword(k: WeaponKit, group: THREE.Object3D, spec: BladeSpec) {
  hilt(k, group, spec);
  straightBlade(k, group, spec);
}

/** A plain swept knuckle bow and pierced shell, shared by the drawn and sheathed rapier. */
export function rapierHilt(k: WeaponKit, group: THREE.Object3D) {
  const grip = .12, from = .085;
  wrapped(k, group, grip);
  k.ellipsoid(group, k.material.iron, 0, grip * .6, 0, .02, .014, .02);
  k.box(group, k.material.iron, .15, .011, .02, 0, -from + .004);
  // The knuckle bow: a round bar from one end of the guard, over the fist, to the pommel.
  const bow = new THREE.CatmullRomCurve3([[.062, -from + .004, 0], [.062, -from + .05, .0], [.04, 0, .0], [.012, grip * .6, 0]].map(([x, y]) => new THREE.Vector3(x, y, 0)));
  k.mesh(group, new THREE.TubeGeometry(bow, 12, .0042, 5), k.material.iron);
  const shell = new THREE.Shape(); shell.absellipse(0, 0, .047, .034, 0, Math.PI * 2, false, 0);
  const hole = new THREE.Path(); hole.absellipse(0, 0, .021, .014, 0, Math.PI * 2, true, 0); shell.holes.push(hole);
  const guard = k.mesh(group, new THREE.ExtrudeGeometry(shell, { depth: .003, bevelEnabled: true, bevelSize: .001, bevelThickness: .001, bevelSegments: 1, steps: 1 }), k.material.iron, 0, -from + .013, 0);
  guard.rotation.x = Math.PI / 2;
}

/** A thin, stiff thrusting blade with a knuckle bow. */
function rapier(k: WeaponKit, group: THREE.Object3D) {
  const from = .085, length = .86, tip = .02, w = .0105;
  rapierHilt(k, group);
  forgedBlade(k, group, [[-from, w, .0055], [-from - length * .25, w * .85, .0048], [-from - length, w * .27, .002], [-from - length - tip, .00015, .0002]], false);
}

/** A bar of ash, thicker toward the middle, bound at the grip and shod in iron; the staff is longer than its wielder's shoulder. */
function staff(k: WeaponKit, group: THREE.Object3D) {
  const butt = .55, head = -1.15;
  haft(k, group, butt, head, GRIP_R * 1.06, 10);
  wrapped(k, group, .16, 0); wrapped(k, group, .16, -.36);
  ring(k, group, butt - .02, GRIP_R, .045); ring(k, group, head + .02, GRIP_R, .045);
  for (const at of [butt - .065, head + .065]) ring(k, group, at, GRIP_R * .94, .009);
}

/** A spear: ash shaft from the iron-shod butt (+Y) down to the socket `socket` metres below the grip, and a leaf head of `headLength` on it. */
function spearOf(k: WeaponKit, group: THREE.Object3D, butt: number, socket: number, headLength: number, half: number) {
  haft(k, group, butt, -socket, GRIP_R, 8);
  wrapped(k, group, .16, 0);
  ring(k, group, butt - .02, GRIP_R, .04);
  // The head is a socketed leaf, lashed on with cord.
  ring(k, group, -socket - .02, GRIP_R * .9, .08);
  if (!k.rough) k.tube(group, k.material.cord, GRIP_R + .002, GRIP_R + .002, .014, 0, -socket + .03, 0, 8);
  leaf(k, group, socket + .02, headLength, half);
}

/** `points` cut at the vertical line `x`, keeping the side beyond it (`above`) or before it. */
function clipX(points: V2[], x: number, above: boolean): V2[] {
  const inside = ([px]: V2) => above ? px >= x : px <= x, out: V2[] = [];
  points.forEach((a, i) => {
    const b = points[(i + 1) % points.length];
    if (inside(a)) out.push(a);
    if (inside(a) !== inside(b)) out.push([x, a[1] + (b[1] - a[1]) * (x - a[0]) / (b[0] - a[0])]);
  });
  return out;
}
/** `points` with a vertex at least every `step` along each side. */
const densify = (points: V2[], step: number): V2[] => points.flatMap((a, i) => {
  const b = points[(i + 1) % points.length], n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / step));
  return Array.from({ length: n }, (_, j) => [a[0] + (b[0] - a[0]) * j / n, a[1] + (b[1] - a[1]) * j / n] as V2);
});
/** A plate whose thickness (±Z) follows `thick(x)`: a wedge, where the thickness falls to a fine edge. */
function wedge(k: WeaponKit, group: THREE.Object3D, material: THREE.Material, points: V2[], thick: (x: number) => number) {
  const geometry = tileWeaponPlate(new THREE.ExtrudeGeometry(outline(densify(points, .012)), { depth: 1, bevelEnabled: false, steps: 1 }));
  const at = geometry.attributes.position;
  for (let i = 0; i < at.count; i++) at.setZ(i, (at.getZ(i) - .5) * thick(at.getX(i)));
  geometry.computeVertexNormals();
  return k.mesh(group, geometry, material);
}

/**
 * An axe head: the haft passes through an eye, the head flares toward +X to a long edge between two horns; the edge is a steel bit forged
 * into the iron and ground down to a wedge, so it is thin and sharp where it meets the cut. `top` and `bottom` are the haft positions
 * of the horns, `reachX` how far the edge stands out, `backSpike` the length of a spike at the back (-X).
 */
function axeHead(k: WeaponKit, group: THREE.Object3D, top: number, bottom: number, reachX: number, backSpike = 0) {
  const eye = (top + bottom) / 2, span = top - bottom, flare = reachX * .15, tipX = reachX + .012;
  const points: V2[] = [[-.018, top - span * .22], [.012, top - span * .04], [reachX * .5, top + span * .08], [reachX, top + flare * .2],
    [tipX, top - span * .3], [tipX + .004, eye], [tipX, bottom + span * .3], [reachX, bottom - flare * .2],
    [reachX * .67, bottom + span * .10], [reachX * .38, bottom + span * .32], [.012, bottom + span * .38], [-.018, bottom + span * .22]];
  const seam = reachX * .78;
  const body = (x: number) => x < .03 ? .02 : .02 - .008 * Math.min(1, (x - .03) / (seam - .03));
  wedge(k, group, k.material.iron, clipX(points, seam, false), body);
  wedge(k, group, k.material.blade, clipX(points, seam, true), x => Math.max(.0012, .012 * Math.max(0, 1 - (x - seam) / (tipX + .004 - seam)) ** 1.15));
  if (backSpike) plate(k, group, k.material.iron, [[-.016, eye + .03], [-backSpike, eye], [-.016, eye - .03]], .012, .002);
  k.tube(group, k.material.iron, GRIP_R * 1.5, GRIP_R * 1.6, span * .3, 0, eye, 0, 8);
  for (const side of [-1, 1]) weaponBox(k, group, k.material.iron, .018, span * .5, .003, 0, eye + span * .1, side * .022);
}

function sickle(k: WeaponKit, group: THREE.Object3D) {
  k.tube(group, k.material.wood, GRIP_R * .96, GRIP_R, .26, 0, -.04, 0, 8);
  wrapped(k, group, .12, 0);
  ring(k, group, .085, GRIP_R, .022);
  // A steel ring with a piece missing: the blade runs from the handle round nearly a full turn, deepest at the belly, its edge on the inside, and
  // ends in a point that stops short of the handle again.
  const cx = .115, cy = -.175, outer = .12, steps = 26, from = Math.PI * .99, to = Math.PI * 2.7, ring2: V2[] = [], inside: V2[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps, a = from + (to - from) * t, width = .028 * Math.sin(Math.PI * t ** .8) + .014 * (1 - t) ** 2;
    ring2.push([cx + Math.cos(a) * outer, cy + Math.sin(a) * outer]);
    inside.push([cx + Math.cos(a) * (outer - width), cy + Math.sin(a) * (outer - width)]);
  }
  const blade = new THREE.ExtrudeGeometry(outline([...ring2, ...inside.reverse()]), { depth: 1, bevelEnabled: false, steps: 1 });
  const at = blade.getAttribute('position');
  for (let i = 0; i < at.count; i++) {
    const dx = at.getX(i) - cx, dy = at.getY(i) - cy, radius = Math.hypot(dx, dy);
    let angle = Math.atan2(dy, dx); while (angle < from - .001) angle += Math.PI * 2;
    const t = THREE.MathUtils.clamp((angle - from) / (to - from), 0, 1), width = .028 * Math.sin(Math.PI * t ** .8) + .014 * (1 - t) ** 2;
    const thickness = .0006 + .0054 * THREE.MathUtils.clamp((radius - outer + width) / Math.max(.001, width), 0, 1);
    at.setZ(i, (at.getZ(i) - .5) * thickness);
  }
  blade.computeVertexNormals(); k.mesh(group, tileWeaponPlate(blade), k.material.blade);
  ring(k, group, -.15, GRIP_R * .8, .035);
  for (const side of [-1, 1]) k.ellipsoid(group, k.material.iron, 0, -.13, side * .017, .004, .004, .002);
}

function club(k: WeaponKit, group: THREE.Object3D) {
  const profile: V2[] = [[.0001, .1], [.021, .095], [.0165, .07], [.0155, -.1], [.024, -.25], [.04, -.36], [.046, -.44], [.034, -.5], [.0001, -.515]];
  lathe(k, group, k.material.wood, profile, 14);
  wrapped(k, group, .17, -.01);
  // A thong loop at the butt.
  k.mesh(group, new THREE.TorusGeometry(.016, .0028, 5, 10), k.material.cord, 0, .11, 0).rotation.y = Math.PI / 2;
  if (k.rough) for (let i = 0; i < 5; i++) {
    const turn = i / 5 * Math.PI * 2, nail = k.mesh(group, new THREE.ConeGeometry(.0085, .04, 4), k.material.iron, Math.sin(turn) * .047, -.4 - (i % 2) * .05, Math.cos(turn) * .047);
    nail.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(Math.sin(turn), 0, Math.cos(turn)));
  } else for (const at of [-.31, -.43]) {
    k.tube(group, k.material.iron, .038 + (at < -.4 ? .005 : -.01), .038 + (at < -.4 ? .004 : -.012), .018, 0, at, 0, 14);
    for (let i = 0; i < 4; i++) { const a = i * Math.PI / 2; k.ellipsoid(group, k.material.blade, Math.cos(a) * (at < -.4 ? .043 : .028), at, Math.sin(a) * (at < -.4 ? .043 : .028), .004, .004, .004); }
  }
}

function mace(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .1, -.52);
  wrapped(k, group, .17, -.0);
  ring(k, group, .09, GRIP_R, .03);
  // A flanged head of forged iron: a core with six plates standing out of it, and a short spike on top.
  lathe(k, group, k.material.iron, [[.0001, -.5], [.026, -.5], [.032, -.54], [.032, -.6], [.024, -.65], [.0001, -.65]], 8);
  for (let i = 0; i < 6; i++) {
    const turn = i / 6 * Math.PI * 2, flange = plate(k, group, k.material.blade, [[.024, -.515], [.047, -.53], [.063, -.552], [.065, -.597], [.047, -.628], [.024, -.64]], .008, .002);
    flange.rotation.y = turn;
  }
  k.mesh(group, new THREE.ConeGeometry(.014, .06, 6), k.material.iron, 0, -.68, 0).rotation.x = Math.PI;
  for (const at of [-.5, -.646]) ring(k, group, at, .025, .014);
}

function handAxe(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .09, -.36, GRIP_R, 8);
  wrapped(k, group, .15, 0);
  ring(k, group, .085, GRIP_R, .025);
  axeHead(k, group, -.25, -.39, .1);
}

function battleaxe(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .32, -.72);
  wrapped(k, group, .16, 0); wrapped(k, group, .14, -.34);
  ring(k, group, .31, GRIP_R, .04);
  axeHead(k, group, -.46, -.78, .17, .09);
}

function greataxe(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .36, -1.0, GRIP_R * 1.06, 9);
  wrapped(k, group, .17, 0); wrapped(k, group, .15, -.36);
  ring(k, group, .35, GRIP_R, .05);
  // Double bitted: a bit each way, the second the mirror of the first.
  axeHead(k, group, -.6, -1.0, .2);
  const other = new THREE.Group(); other.scale.x = -1; group.add(other); axeHead(k, other, -.6, -1.0, .2);
}

function greatmaul(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .34, -1.06, GRIP_R * 1.06, 9);
  wrapped(k, group, .17, 0); wrapped(k, group, .15, -.36);
  ring(k, group, .33, GRIP_R, .05);
  // A block of iron through the haft, banded at both strike faces, with a wedge locking it.
  weaponBox(k, group, k.material.iron, .34, .135, .125, 0, -1.02);
  for (const side of [-1, 1]) {
    weaponBox(k, group, k.material.blade, .036, .145, .14, side * .172, -1.02);
    weaponBox(k, group, k.material.iron, .025, .15, .145, side * .135, -1.02);
    k.ellipsoid(group, k.material.blade, side * .05, -.947, 0, .008, .003, .008);
  }
  k.tube(group, k.material.wood, GRIP_R * .9, GRIP_R * .9, .06, 0, -1.11, 0, 8);
  k.box(group, k.material.wood, .012, .06, .16, 0, -.94);
}

function poleHammer(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .46, -1.3);
  wrapped(k, group, .16, 0); wrapped(k, group, .14, -.36);
  ring(k, group, .45, GRIP_R, .04);
  // Langets run down the haft from the head, so a blow cannot cut it.
  for (const side of [-1, 1]) k.box(group, k.material.iron, .006, .42, .012, side * (GRIP_R + .003), -1.1);
  ring(k, group, -1.28, GRIP_R, .09);
  weaponBox(k, group, k.material.iron, .1, .063, .056, .06, -1.3);
  weaponBox(k, group, k.material.blade, .018, .075, .065, .112, -1.3);
  k.mesh(group, new THREE.ConeGeometry(.022, .15, 6), k.material.iron, -.075, -1.3, 0).rotation.z = Math.PI / 2;   // a beak behind it
  k.mesh(group, new THREE.ConeGeometry(.02, .22, 6), k.material.iron, 0, -1.455, 0).rotation.x = Math.PI;         // a spike on top
}

function glaive(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .46, -1.22);
  wrapped(k, group, .16, 0); wrapped(k, group, .14, -.36);
  ring(k, group, .45, GRIP_R, .04);
  ring(k, group, -1.2, GRIP_R, .09);
  // A single-edged, curved blade on a socket, its edge toward +X.
  wedge(k, group, k.material.blade, [[-.02, -1.24], [.012, -1.245], [.05, -1.3], [.082, -1.43], [.082, -1.58], [.05, -1.72], [-.005, -1.84], [-.015, -1.7], [-.02, -1.5]], x => .001 + .009 * (1 - THREE.MathUtils.clamp((x + .02) / .102, 0, 1)));
  for (const side of [-1, 1]) weaponBox(k, group, k.material.iron, .009, .24, .014, side * .017, -1.09);
}

function greatSpear(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .56, -1.4, GRIP_R * 1.06, 9);
  wrapped(k, group, .17, 0); wrapped(k, group, .15, -.44);
  ring(k, group, .55, GRIP_R, .05);
  ring(k, group, -1.42, GRIP_R, .1);
  leaf(k, group, 1.4, .3, .037);
  for (const side of [-1, 1]) k.box(group, k.material.iron, .006, .3, .012, side * (GRIP_R + .003), -1.2);
}

function greatsword(k: WeaponKit, group: THREE.Object3D) {
  const spec: BladeSpec = { grip: .28, guard: .3, from: .16, length: .96, width: .033, tip: .12 };
  hilt(k, group, spec);
  straightBlade(k, group, spec);
}

function throwingHammer(k: WeaponKit, group: THREE.Object3D) {
  haft(k, group, .13, -.22, .0135, 8);
  wrapped(k, group, .1, 0, .0145);
  k.ellipsoid(group, k.material.iron, 0, .13, 0, .02, .012, .02);
  weaponBox(k, group, k.material.iron, .085, .05, .05, .01, -.24);
  weaponBox(k, group, k.material.blade, .012, .056, .056, .056, -.24);
  k.mesh(group, new THREE.ConeGeometry(.018, .05, 5), k.material.iron, -.07, -.24, 0).rotation.z = Math.PI / 2;
}

/** The bows' half length by part (m): the string runs tip to tip. */
export const BOW_HALF: Record<string, number> = { shortbow: .56, longbow: .78 };
/** A self bow: a stave of yew that tapers to horn nocks, with a leather-wrapped riser. */
export function bow(k: WeaponKit, group: THREE.Object3D, half = BOW_HALF.shortbow) {
  const spread = half / .56, curve = new THREE.CatmullRomCurve3([new THREE.Vector3(0, -half, -.21), new THREE.Vector3(0, -half * .625, -.04 * spread), new THREE.Vector3(),
    new THREE.Vector3(0, half * .625, -.04 * spread), new THREE.Vector3(0, half, -.21)]);
  const stave = taperedTube(curve, t => (.015 - .0062 * Math.abs(2 * t - 1) ** 1.3) * (half > .6 ? 1.04 : 1), 40, 10);
  // Flattened limbs bend through their belly; the riser stays round enough for the hand.
  const at = stave.getAttribute('position'); for (let i = 0; i < at.count; i++) at.setX(i, at.getX(i) * 1.3); stave.computeVertexNormals();
  k.mesh(group, stave, k.material.wood);
  wrapped(k, group, .13, 0, .019);
  if (k.rough) { for (const at of [-.072, .072]) k.tube(group, k.material.cord, .0205, .0205, .01, 0, at, 0, 8); return; }
  // The tips carry horn nocks.
  for (const end of [-1, 1]) k.ellipsoid(group, k.material.bone, 0, end * (half + .005), -.214, .009, .02, .009);
  for (const end of [-1, 1]) k.tube(group, k.material.cord, .011, .011, .025, 0, end * (half - .02), -.196, 10);
}

/** The nut the string is drawn back into, and the height of the rail above the grip: where a bolt's nock lies (bow joint space). */
export const BOLT_NOCK = { y: .146, z: -.04 };
/** Half widths of the crossbows' prods (m), by part. */
const PROD = { crossbow: { half: .3, back: .16, front: .25, stock: .55, grip: .3 }, ballista: { half: .42, back: .2, front: .33, stock: .82, grip: .38 } } as const;
/**
 * A crossbow, levelled on the bow joint: +Z toward the target, +Y up. The fore grip is a turned post under the stock, held by the left hand at
 * the origin; the rear grip is another, `stock` behind it, for the right. The prod crosses the stock ahead, strung back to the nut, a bolt on the rail.
 */
export function crossbow(k: WeaponKit, group: THREE.Object3D, heavy = false) {
  const m = heavy ? PROD.ballista : PROD.crossbow, rail = .105;
  // Stock: a long block of oak, deeper at the rear where it meets the shoulder.
  const front = .26, rear = -m.stock;
  // Drawn in side view (x along the stock, y up) and turned to lie along +Z.
  const side = new THREE.Group(); side.rotation.y = -Math.PI / 2; group.add(side);
  plate(k, side, k.material.wood, [[rear, .052], [rear, .131], [rear * .82, .139], [rear * .48, .119], [front * .3, .114], [front, .11], [front, .092], [front * .3, .086], [rear * .42, .076], [rear * .72, .053]], heavy ? .06 : .045, .004);
  // A spring-steel prod, broad and thin through its bending axis.
  const prod = new THREE.CatmullRomCurve3([new THREE.Vector3(-m.half, rail + .01, m.back), new THREE.Vector3(-m.half * .55, rail, m.front - .04), new THREE.Vector3(0, rail, m.front),
    new THREE.Vector3(m.half * .55, rail, m.front - .04), new THREE.Vector3(m.half, rail + .01, m.back)]);
  const limb = taperedTube(prod, t => (heavy ? .017 : .0135) - .005 * Math.abs(2 * t - 1) ** 1.4, 32, 8);
  const positions = limb.getAttribute('position'); for (let i = 0; i < positions.count; i++) positions.setY(i, rail + (positions.getY(i) - rail) * .4); limb.computeVertexNormals();
  k.mesh(group, limb, k.material.blade);
  for (const side of [-1, 1]) {
    weaponBox(k, group, k.material.iron, .004, .04, .22, side * (heavy ? .033 : .026), .098, -.10);
    for (const z of [-.18, -.03]) k.ellipsoid(group, k.material.blade, side * (heavy ? .036 : .029), .097, z, .003, .004, .004);
  }
  // A stirrup at the front to brace the foot while spanning.
  const stirrup = k.mesh(group, new THREE.TorusGeometry(.04, .0055, 5, 14, Math.PI), k.material.iron, 0, rail, m.front + .06); stirrup.rotation.set(0, 0, Math.PI / 2);
  stirrup.scale.set(1, 1, 1);
  // String from tip to tip, drawn back into the nut.
  const nut = BOLT_NOCK.z, pointsA = [new THREE.Vector3(-m.half, rail + .01, m.back), new THREE.Vector3(0, rail + .014, nut)], pointsB = [new THREE.Vector3(m.half, rail + .01, m.back), new THREE.Vector3(0, rail + .014, nut)];
  for (const points of [pointsA, pointsB]) {
    const [a, b] = points, length = a.distanceTo(b), line = k.tube(group, k.material.cord, .0016, .0016, length, 0, 0, 0, 4);
    line.position.copy(a).add(b).multiplyScalar(.5); line.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  }
  weaponBox(k, group, k.material.iron, .03, .02, .05, 0, .13, nut);
  // Fore grip and rear grip, turned posts of ash wrapped in leather.
  k.tube(group, k.material.wood, .019, .02, .16, 0, .0, 0, 10);
  k.tube(group, k.material.wood, .019, .02, .16, 0, .0, -m.grip, 10);
  k.tube(group, k.material.leather, .0205, .0205, .09, 0, -.0, 0, 10);
  k.box(group, k.material.iron, .012, .05, .014, 0, .02, -m.grip + .03);    // the trigger lever
  const triggerGuard = new THREE.CatmullRomCurve3([[0,.067,-m.grip+.07],[0,-.02,-m.grip+.065],[0,-.045,-m.grip+.015],[0,.065,-m.grip-.025]].map(p=>new THREE.Vector3(...p)));
  k.mesh(group, new THREE.TubeGeometry(triggerGuard, 16, .003, 6), k.material.iron);
  // A rail of bone along the top, where the bolt (`bolt`, which the animator shows) lies.
  k.box(group, k.material.bone, .012, .008, m.stock * .55, 0, .133, -m.stock * .02 + .03);
  if (heavy) {
    // A ratchet wheel and crank at the rear spans the string.
    k.tube(group, k.material.iron, .04, .04, .012, .032, rail, -m.stock * .62, 14).rotation.z = Math.PI / 2;
    k.tube(group, k.material.iron, .006, .006, .09, .044, rail, -m.stock * .62 - .045, 6).rotation.set(Math.PI / 2, 0, 0);
  }
}

/** A bolt the crossbow shoots: shorter and thicker than an arrow, fletched with two feathers. Local +Z is the point, the nock at the origin. */
export function bolt(k: WeaponKit, group: THREE.Object3D, heavy = false) {
  const length = heavy ? .46 : .36;
  const shaft = k.tube(group, k.material.wood, .006, .006, length, 0, 0, length / 2, 6); shaft.rotation.x = Math.PI / 2;
  const head = k.mesh(group, new THREE.ConeGeometry(heavy ? .016 : .012, .05, 4), k.material.blade, 0, 0, length + .02); head.rotation.x = Math.PI / 2;
  for (const turn of [0, Math.PI]) k.box(group, k.material.feather, .028, .004, .07, 0, 0, .05).rotation.z = turn;
}

/** A belt case of turned wood with bolts standing in it, for a crossbow. */
export function boltCase(k: WeaponKit, group: THREE.Object3D) {
  k.tube(group, k.material.leather, .042, .036, .2, 0, 0, 0, 10);
  k.tube(group, k.material.cord, .045, .045, .014, 0, .09, 0, 10);
  for (let i = 0; i < 4; i++) { const x = (i % 2) * .03 - .015, z = Math.floor(i / 2) * .028 - .014; k.tube(group, k.material.wood, .0055, .0055, .09, x, .13, z, 6); k.box(group, k.material.feather, .02, .03, .004, x, .17, z); }
}

/** How a part is drawn into the rig's prop group of that name. */
export const WEAPON_MODELS: Record<string, (k: WeaponKit, group: THREE.Object3D, spec?: BladeSpec) => void> = {
  sickle, club, mace, hand_axe: handAxe, rapier,
  spear: (k, g) => spearOf(k, g, .56, 1.2, .26, .034),
  staff, battleaxe, pole_hammer: poleHammer, glaive, great_spear: greatSpear, greatmaul, greataxe, greatsword,
  throwing_hammer: throwingHammer,
  longbow: (k, g) => bow(k, g, BOW_HALF.longbow),
  crossbow: (k, g) => crossbow(k, g, false), ballista: (k, g) => crossbow(k, g, true),
};

/** Where a held part runs along its local Y (m, the grip at 0, the head down -Y), and its guard's half width. For the head-clearance check. */
export interface HeldSpan { top: number; bottom: number; guard?: { y: number; half: number } }
export const HELD_WEAPONS: Record<string, HeldSpan> = {
  sickle: { top: .1, bottom: -.28 }, club: { top: .12, bottom: -.52 }, mace: { top: .1, bottom: -.68 }, hand_axe: { top: .09, bottom: -.39 },
  rapier: { top: .08, bottom: -.97, guard: { y: -.085, half: .075 } }, spear: { top: .56, bottom: -1.46 }, staff: { top: .55, bottom: -1.15 },
  battleaxe: { top: .32, bottom: -.78 }, pole_hammer: { top: .46, bottom: -1.46 }, glaive: { top: .46, bottom: -1.84 }, great_spear: { top: .56, bottom: -1.74 },
  greatmaul: { top: .34, bottom: -1.1 }, greataxe: { top: .36, bottom: -1.0 }, greatsword: { top: .17, bottom: -1.24, guard: { y: -.16, half: .15 } },
  throwing_hammer: { top: .13, bottom: -.27 },
};

/**
 * Lay a weapon on the wearer's back (torso space, +Z the chest): its head (-Y) toward `head`, its flats (+Z) toward `flat`. The rest
 * of the orientation follows from those two.
 */
export function lieOnBack(group: THREE.Object3D, head: THREE.Vector3, flat: THREE.Vector3) {
  const y = head.clone().normalize().multiplyScalar(-1), z = flat.clone().normalize(), x = new THREE.Vector3().crossVectors(y, z).normalize();
  group.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, new THREE.Vector3().crossVectors(z, x), z));
}
